import {useEffect} from 'react';
import {trackTealiumSubscriptionCompleted} from '@src/Tracking/tealiumEvents';

const VODACOM_SSO_DONE_KEY = 'vodacom_sso_done';
const VODACOM_SUBSCRIPTION_TRACKED_KEY = 'vodacom_subscription_completed_tracked';

/**
 * Hook for vc.trace.plus:
 * - No auto-redirect to Penrose. Users browse normally until they sign in
 *   via the "Sign in with Vodacom" button in the auth modal.
 * - When e-msisdn/msisdn params arrive (Penrose callback), marks SSO as
 *   in progress so useVodacomAutoLogin can handle it without interference.
 * - When Penrose appends result=active (successful subscription only —
 *   never on a plain returning-user redirect), fires subscription_completed
 *   to Tealium. Kept isolated from useVodacomAutoLogin so this can never
 *   interfere with the login flow.
 */
export function useVodacomRedirect() {
  useEffect(() => {
    // Fail closed: this hook must never throw — a bad URL, a blocked
    // sessionStorage (private browsing, embedded iframe), or a rejected
    // history.replaceState call should degrade silently, not crash the app.
    try {
      if (typeof window === 'undefined') return;
      if (window.location.hostname !== 'vc.trace.plus') return;

      const url = new URL(window.location.href);
      const hasMsisdn: boolean = url.searchParams.has('msisdn');
      const hasEMsisdn: boolean = url.searchParams.has('e-msisdn');

      if (hasMsisdn || hasEMsisdn) {
        try {
          sessionStorage.setItem(VODACOM_SSO_DONE_KEY, '1');
        } catch { /* noop */ }
      }

      const result: string | null = url.searchParams.get('result');
      if (result === 'active') {
        const subscriptionId: string | undefined =
          url.searchParams.get('subscription-id') || undefined;

        let alreadyTracked = false;
        try {
          alreadyTracked =
            !!subscriptionId &&
            sessionStorage.getItem(VODACOM_SUBSCRIPTION_TRACKED_KEY) === subscriptionId;
        } catch { /* noop */ }

        if (!alreadyTracked) {
          try {
            trackTealiumSubscriptionCompleted({subscriptionId});
            if (subscriptionId) {
              sessionStorage.setItem(VODACOM_SUBSCRIPTION_TRACKED_KEY, subscriptionId);
            }
          } catch { /* noop */ }
        }

        try {
          url.searchParams.delete('result');
          url.searchParams.delete('subscription-id');
          window.history.replaceState({}, '', url.toString());
        } catch { /* noop */ }
      }
    } catch { /* noop */ }
  }, []);
}
